import type { TeamTag, Workitem, WorkitemTag } from '@rhythmic/db/types'
import type { KanbanContext, KanbanWorkitemMutationPayload, MUTABLE_WORKITEM_FIELD_KEYS } from '@rhythmic/types'
import { generateUuid, KanbanContextSchema, KanbanWorkitemMutationSchema } from '@rhythmic/types'
import type { Collection, Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import {
  getBoardsCollection,
  getTeamTagsCollection,
  getWorkitemsCollection,
  getWorkitemTagsCollection,
} from '../getCollections'

const logger = useRhythmicLogger('useKanbanBoardContext:updateWorkitem')

type MutableWorkitemFields = Partial<Pick<Workitem, (typeof MUTABLE_WORKITEM_FIELD_KEYS)[number]>> & {
  workitemTagsToAdd?: Array<{
    tagId: string
    tag?: Omit<TeamTag, 'created_at' | 'updated_at' | 'workitem_tags'>
    workspaceId?: string | null
    workitemTagId?: string
  }>
  workitemTagsToRemove?: Array<{ eid: string; tagId?: string }>
  teamTagsToCreate?: Array<Omit<TeamTag, 'created_at' | 'updated_at' | 'workitem_tags'>>
}

type TagReferenceInput = string | { id: string; workitemTagId?: string; tagId?: string }

interface TagCollectionOverrides {
  workitemsCollection?: Collection<Workitem>
  workitemTagsCollection?: Collection<WorkitemTag>
  teamTagsCollection?: Collection<TeamTag>
}

function normalizeTagReference(entry: TagReferenceInput): {
  id: string
  workitemTagId?: string
  tagId?: string
} {
  if (typeof entry === 'string') return { id: entry }

  return entry
}

function applyTagMutations(mutations: MutableWorkitemFields, context: KanbanContext) {
  const rawTagMutations = (mutations as Record<string, any>).tags as
    | {
        add?: TagReferenceInput[]
        remove?: TagReferenceInput[]
        create?: Array<{ id?: string; tag: string; color?: string | null }>
      }
    | undefined

  if (!rawTagMutations) return

  const additions = (rawTagMutations.add ?? []).map(normalizeTagReference)
  const removals = (rawTagMutations.remove ?? []).map(normalizeTagReference)
  const creations = rawTagMutations.create ?? []

  if (additions.length > 0) {
    mutations.workitemTagsToAdd = [
      ...(mutations.workitemTagsToAdd ?? []),
      ...additions.map((entry) => ({
        tagId: entry.id,
        workspaceId: context.workspaceId,
      })),
    ]
  }

  if (removals.length > 0) {
    mutations.workitemTagsToRemove = [
      ...(mutations.workitemTagsToRemove ?? []),
      ...removals.map((entry) => ({
        eid: entry.id,
        tagId: entry.tagId,
      })),
    ]
  }

  if (creations.length > 0) {
    const createdTags = creations.map((item) => {
      const resolvedId = item.id ?? generateUuid()
      return {
        id: resolvedId,
        tag: item.tag,
        color: item.color ?? 'bg-surface-500 dark:bg-surface-700',
        team_id: context.teamId,
        workspace_id: context.workspaceId,
        type: null,
      } satisfies Omit<TeamTag, 'created_at' | 'updated_at' | 'workitem_tags'>
    })

    mutations.teamTagsToCreate = [...(mutations.teamTagsToCreate ?? []), ...createdTags]

    mutations.workitemTagsToAdd = [
      ...(mutations.workitemTagsToAdd ?? []),
      ...createdTags.map((tag) => ({
        tagId: tag.id,
        tag,
        workspaceId: context.workspaceId,
        workitemTagId: generateUuid(),
      })),
    ]
  }

  delete (mutations as Record<string, any>).tags
}

function ensureCollections(context: KanbanContext, overrides?: TagCollectionOverrides) {
  const { packageId, teamId } = context
  const boardsCollection = getBoardsCollection(packageId)
  const workitemsCollection = overrides?.workitemsCollection ?? getWorkitemsCollection(teamId)
  const workitemTagsCollection = overrides?.workitemTagsCollection ?? getWorkitemTagsCollection(teamId)
  const teamTagsCollection = overrides?.teamTagsCollection ?? getTeamTagsCollection(teamId)

  return {
    boardsCollection,
    workitemsCollection,
    workitemTagsCollection,
    teamTagsCollection,
  }
}

function buildWorkitemRequestBody(workitemId: string, mutations: MutableWorkitemFields) {
  const basePayload: Record<string, unknown> = { id: workitemId }
  for (const [key, value] of Object.entries(mutations)) {
    if (value === undefined) continue

    if (key === 'teamTagsToCreate' || key === 'workitemTagsToAdd' || key === 'workitemTagsToRemove' || key === 'tags')
      continue

    if (key === 'last_synced_date') continue

    basePayload[key] = value
  }

  return basePayload
}

export function handleUpdateWorkitem(
  payload: KanbanWorkitemMutationPayload,
  context: KanbanContext | undefined,
  collectionsOverride?: TagCollectionOverrides,
): Transaction {
  if (!context) throw new Error('Kanban board context is not ready: missing context for updating a workitem')

  const parsedContext = KanbanContextSchema.parse(context)
  logger.debug('Updating workitem with payload', {
    payload,
    context: parsedContext,
  })
  const parsedPayload = KanbanWorkitemMutationSchema.parse(payload)
  const updatePayload = {
    ...parsedPayload,
    mutations: (parsedPayload.mutations ?? {}) as MutableWorkitemFields,
  }

  // Never allow last_synced_date to flow through client mutations;
  // this field is managed exclusively by sync endpoints.
  if ('last_synced_date' in updatePayload.mutations)
    delete (updatePayload.mutations as Record<string, unknown>).last_synced_date

  applyTagMutations(updatePayload.mutations, parsedContext)

  const collections = ensureCollections(parsedContext, collectionsOverride)
  const workitemsCollection = collections.workitemsCollection

  const action = createOptimisticAction<typeof updatePayload>({
    onMutate: (mutationPayload: typeof updatePayload) => {
      logger.debug('Applying optimistic workitem update', mutationPayload)
      workitemsCollection.update(mutationPayload.workitemId, (draft: Workitem) => {
        const { teamTagsToCreate, workitemTagsToAdd, workitemTagsToRemove, last_synced_date, ...rest } =
          mutationPayload.mutations
        Object.assign(draft as Record<string, unknown>, rest)

        const anyDraft = draft as unknown as { last_synced_date?: unknown }
        if (typeof anyDraft.last_synced_date === 'string') {
          const parsed = new Date(anyDraft.last_synced_date)
          anyDraft.last_synced_date = Number.isNaN(parsed.getTime()) ? null : parsed
        }
      })
      // Handle team tag creations
      if (mutationPayload.mutations.teamTagsToCreate && mutationPayload.mutations.teamTagsToCreate.length > 0) {
        for (const teamTagData of mutationPayload.mutations.teamTagsToCreate) {
          const newTeamTagId = teamTagData.id ?? generateUuid()
          collections.teamTagsCollection.insert({
            ...teamTagData,
            id: newTeamTagId,
            created_at: new Date(),
            updated_at: new Date(),
          })
        }
      }
      // Handle workitem tag additions
      if (mutationPayload.mutations.workitemTagsToAdd && mutationPayload.mutations.workitemTagsToAdd.length > 0) {
        for (const tagData of mutationPayload.mutations.workitemTagsToAdd) {
          collections.workitemTagsCollection.insert({
            eid: tagData.workitemTagId ?? generateUuid(),
            team_id: parsedContext.teamId,
            workspace_id: tagData.workspaceId ?? parsedContext.workspaceId,
            workitem_id: mutationPayload.workitemId,
            tag_id: tagData.tagId,
          })
        }
      }
      // Handle workitem tag removals
      if (mutationPayload.mutations.workitemTagsToRemove && mutationPayload.mutations.workitemTagsToRemove.length > 0) {
        for (const tagRef of mutationPayload.mutations.workitemTagsToRemove) {
          logger.debug('Optimistic workitem tag removal', tagRef)
          const key = tagRef.eid
          if (key) collections.workitemTagsCollection.delete(key)
        }
      }
    },
    mutationFn: async (mutationPayload: typeof updatePayload) => {
      const waitables = []
      const workitemRequestBody = buildWorkitemRequestBody(mutationPayload.workitemId, mutationPayload.mutations)
      if (workitemRequestBody && Object.keys(workitemRequestBody).length > 1) {
        const result = await $fetch<{
          message: string
          txid: string
          workitem: Partial<Workitem>
        }>(`/api/workitems/${mutationPayload.workitemId}` as string, {
          method: 'PUT',
          body: workitemRequestBody,
        })
        logger.debug('Workitem update API result:', result)
        if (workitemsCollection.utils?.awaitTxId) {
          waitables.push(workitemsCollection.utils.awaitTxId(Number.parseInt(result.txid)))
        }
      }
      if (mutationPayload.mutations.teamTagsToCreate && mutationPayload.mutations.teamTagsToCreate.length > 0) {
        for (const teamTagData of mutationPayload.mutations.teamTagsToCreate) {
          const result = await $fetch<{ txid?: string }>(`/api/team/${parsedContext.teamId}/tags` as string, {
            method: 'POST',
            body: JSON.stringify({
              ...teamTagData,
              team_id: parsedContext.teamId,
              workspace_id: parsedContext.workspaceId,
            }),
          })
          if (collections.teamTagsCollection.utils?.awaitTxId && result.txid) {
            waitables.push(collections.teamTagsCollection.utils.awaitTxId(Number.parseInt(result.txid)))
          }
        }
      }
      if (mutationPayload.mutations.workitemTagsToAdd && mutationPayload.mutations.workitemTagsToAdd.length > 0) {
        for (const tagData of mutationPayload.mutations.workitemTagsToAdd) {
          const result = await $fetch<{ txid?: string }>(
            `/api/workitems/${mutationPayload.workitemId}/tags` as string,
            {
              method: 'POST',
              body: JSON.stringify({
                team_id: parsedContext.teamId,
                workspace_id: tagData.workspaceId ?? parsedContext.workspaceId,
                workitem_id: mutationPayload.workitemId,
                tag_id: tagData.tagId,
              }),
            },
          )
          if (collections.workitemTagsCollection.utils?.awaitTxId && result.txid) {
            waitables.push(collections.workitemTagsCollection.utils.awaitTxId(Number.parseInt(result.txid)))
          }
        }
      }
      if (mutationPayload.mutations.workitemTagsToRemove && mutationPayload.mutations.workitemTagsToRemove.length > 0) {
        for (const tagRef of mutationPayload.mutations.workitemTagsToRemove) {
          const result = await $fetch<{ txid?: string }>(
            `/api/workitems/${mutationPayload.workitemId}/tags` as string,
            {
              method: 'DELETE',
              body: JSON.stringify({
                team_id: parsedContext.teamId,
                workspace_id: parsedContext.workspaceId,
                workitem_id: mutationPayload.workitemId,
                eid: tagRef.eid,
                tag_id: tagRef.tagId,
              }),
            },
          )
          if (collections.workitemTagsCollection.utils?.awaitTxId && result.txid) {
            waitables.push(collections.workitemTagsCollection.utils.awaitTxId(Number.parseInt(result.txid)))
          }
        }
      }

      if (waitables.length === 0) return { txid: undefined }

      await Promise.all(waitables)

      return { txid: undefined }
    },
  })

  const updateTx = action(updatePayload)

  logger.info('Kanban workitem updated successfully', {
    boardId: parsedContext.boardId,
    workitemId: updatePayload.workitemId,
    updateTx: {
      status: updateTx.isPersisted,
      state: updateTx.state,
    },
  })

  return updateTx
}
